import { Fragment, useEffect, useLayoutEffect, useRef } from "react";
import { Callout } from "@hamlak/ui";
import type { Block, Message } from "@/api/types";
import { t, withJosa } from "@/i18n/ko";
import { BlockRenderer, type Speaker } from "@/features/plot/blocks/BlockRenderer";
import {
  CharacterTurn,
  TurnFailure,
  UserTurn,
  type LongPressTarget,
} from "./CharacterTurn";
import type { RerollState } from "./useRerollStream";
import type { TurnState } from "./useTurnStream";

type Props = {
  firstScene: readonly Block[];
  /** 캐릭터 메시지 who 행(이름·아바타). */
  speaker?: Speaker | undefined;
  /** 회원: GET /conversations messages[]. 비회원은 빈 배열. */
  messages?: readonly Message[];
  /** 진행 중 턴(낙관 사용자 말풍선·스트리밍·실패 Callout). */
  turn?: TurnState | undefined;
  onRetry?: () => void;
  onFocusInput?: () => void;
  onLongPress?: ((target: LongPressTarget) => void) | undefined;
  /** 이전 메시지(D6): 남아 있으면 첫 장면·고지 대신 위쪽에서 자동 로드 */
  older?: { hasMore: boolean; loading: boolean; load: () => void } | undefined;
  /** 답변 다시 생성(CHT-08). onReroll 이 있으면 마지막 완료 캐릭터 답변에만 ↻ (턴·리롤 진행 중에는 숨김) */
  reroll?: RerollState | undefined;
  onReroll?: ((messageId: string) => void) | undefined;
  onRerollRetry?: () => void;
};

const OLDER_TRIGGER_PX = 400; // 문서 위에서 이만큼 안으로 들어오면 이전 메시지를 부른다
const BOTTOM_PX = 40; // 문서 끝에서 이 안이면 "바닥"

const active = (t: TurnState | undefined) =>
  t && t.phase !== "idle" && t.phase !== "done";

/** 문서 끝까지 남은 거리(px). */
const gapToBottom = () => document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
/** 문서 스크롤 위치를 옮긴다. scrollingElement 가 없으면(테스트 환경 등) endRef 로 대신한다. */
const scrollDocTo = (top: number | "bottom", end: HTMLElement | null) => {
  const el = document.scrollingElement;
  if (el?.scrollTo) el.scrollTo({ top: top === "bottom" ? el.scrollHeight : top });
  else if (top === "bottom") end?.scrollIntoView?.({ block: "end" });
};

/**
 * 메시지 영역. AI 고지 Callout + 첫 장면 블록 + messages[](user/character, status) + 진행 중 턴.
 * 스크롤(AC0-231·AC0-285): 채팅방에 들어오면 대화 이력이 있으면 바닥, 없으면(비회원·첫 대화) 맨 위에서 시작한다. 보내거나 답변 다시 생성을 시작하면 바닥으로 가서 새 글자를 따라가고,
 * 사용자가 위로 올리면 그 자리에 둔다(글자가 늘어도 끌어내리지 않음). 바닥까지 다시 내려오거나 새로 보내면 다시 따라간다.
 * 이전 메시지가 남아 있으면 고지·첫 장면은 맨 처음까지 불러온 뒤에 보인다.
 */
export function MessageList({
  firstScene,
  speaker,
  messages = [],
  turn,
  onRetry,
  onFocusInput,
  onLongPress,
  older,
  reroll,
  onReroll,
  onRerollRetry,
}: Props) {
  const endRef = useRef<HTMLDivElement>(null);
  // 새 글자를 따라 문서 끝으로 갈지. 들어올 땐 꺼 두고, 이력 진입(AC0-285)·보내기·다시 생성 시작·바닥 도달 때 켠다(AC0-231)
  const followRef = useRef(false);
  const lastYRef = useRef(0);
  const olderRef = useRef(older);
  // 스크롤 핸들러가 최신 older 를 보게 커밋마다 맞춘다(렌더 중 ref 쓰기 금지)
  useLayoutEffect(() => {
    olderRef.current = older;
  });

  // 입장: 맨 위에서 시작한다(로그인 직후 복귀 포함). SPA 이동은 앞 화면 스크롤 위치를 그대로 물려받아서 명시적으로 올린다
  useLayoutEffect(() => {
    scrollDocTo(0, null);
    lastYRef.current = window.scrollY;
  }, []);

  // 대화 이력이 있으면 처음 받은 때 한 번 바닥으로 가서 따라가기를 켠다(캐시면 마운트 직후, 아니면 도착 때). 이력이 없으면 맨 위(AC0-285)
  const enteredRef = useRef(false);
  const hasMessages = messages.length > 0;
  useLayoutEffect(() => {
    if (enteredRef.current || !hasMessages) return;
    enteredRef.current = true;
    followRef.current = true;
    scrollDocTo("bottom", endRef.current);
    lastYRef.current = window.scrollY;
  }, [hasMessages]);

  useEffect(() => {
    const loadOlderNearTop = () => {
      const o = olderRef.current;
      if (o?.hasMore && !o.loading && window.scrollY < OLDER_TRIGGER_PX) o.load();
    };
    const stopFollowing = () => {
      followRef.current = false;
      loadOlderNearTop(); // 맨 위(scrollY 0)에서는 scroll 이벤트가 안 나서 위로 올리려는 손짓으로도 부른다
    };
    // 예전엔 "바닥에서 120px 안이면 붙어 있음"으로만 판정해서, 스트리밍 중 글자마다(45ms) 바닥으로 다시 끌어내려
    // 120px 을 벗어날 틈이 없었다(위로 못 올림). 이제 위로 올리는 손짓·이동이 보이면 바로 따라가기를 끈다.
    const onScroll = () => {
      const y = window.scrollY;
      const gap = gapToBottom();
      if (y < lastYRef.current && gap > BOTTOM_PX) followRef.current = false; // 위로 이동(스크롤바·키보드·관성 포함). 내용이 줄어 당겨진 경우는 바닥이라 제외
      else if (y > lastYRef.current && gap <= BOTTOM_PX) followRef.current = true; // 바닥까지 다시 내려왔다
      lastYRef.current = y;
      loadOlderNearTop();
    };
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) stopFollowing();
    };
    let touchY: number | null = null;
    const onTouchStart = (e: TouchEvent) => {
      touchY = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY;
      if (y === undefined) return;
      if (touchY !== null && y > touchY) stopFollowing(); // 손가락이 아래로 = 내용을 위로 올려 본다
      touchY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  // 앞에 붙으면(첫 seq 가 작아지면) 불어난 높이만큼 내려 보던 자리를 지킨다. 직전 커밋의 높이를 기억해 둔다
  const firstSeq = messages[0]?.seq;
  const snap = useRef<{ firstSeq: number | undefined; height: number }>({ firstSeq, height: 0 });
  useLayoutEffect(() => {
    const height = document.documentElement.scrollHeight;
    const prev = snap.current;
    if (prev.firstSeq !== undefined && firstSeq !== undefined && firstSeq < prev.firstSeq && prev.height > 0) window.scrollBy(0, height - prev.height);
    snap.current = { firstSeq, height };
  });

  const lastLen = messages.length;
  const streamedLen = turn?.streamed.length ?? 0;
  const phase = turn?.phase;
  const rerollLen = reroll?.streamed.length ?? 0;
  const rerollPhase = reroll?.phase;
  const prevPhases = useRef({ phase, rerollPhase });
  // 보내기(sending 진입, 재시도 포함)·다시 생성 시작(running 진입)이면 따라가기를 켠다. 따라가는 중이면 새 글자마다 문서 끝으로(AC0-142).
  // endRef.scrollIntoView 는 하단 고정(sticky) 입력창 뒤에 마지막 줄·커서가 가려져서, 문서 끝으로 스크롤해 입력창을 제자리에 두고 마지막 줄을 그 위에 보이게 한다.
  useEffect(() => {
    const prev = prevPhases.current;
    prevPhases.current = { phase, rerollPhase };
    if ((phase === "sending" && prev.phase !== "sending") || (rerollPhase === "running" && prev.rerollPhase !== "running")) followRef.current = true;
    if (!followRef.current) return;
    scrollDocTo("bottom", endRef.current);
  }, [lastLen, streamedLen, phase, rerollLen, rerollPhase]);

  const last = messages.at(-1);
  const rerollable = onReroll && !active(turn) && reroll?.phase !== "running" && last?.role === "character" && last.status === "complete" ? last.id : null;
  const rerolling = reroll?.phase === "running" ? reroll.messageId : null;
  const speakerName = speaker?.name ?? "";

  return (
    <section className="chat__messages" data-testid="message-list">
      {/* 시안 F01-12 disclaimer(135:7479): t2 보조색 가운데 한 줄 */}
      {older?.hasMore ? (
        <p className="chat__olderLoading" role="status" aria-hidden={!older.loading}>
          {older.loading ? t("common.loading") : ""}
        </p>
      ) : (
        <>
          <p className="chat__disclaimer">{t("chat.aiNotice")}</p>
          <BlockRenderer blocks={firstScene} speaker={speaker} />
        </>
      )}
      {messages.map((m) =>
        m.role === "user" ? (
          <UserTurn
            key={m.id}
            text={m.content.text}
            pending={m.status === "pending"}
            onLongPress={onLongPress}
            messageId={m.id}
          />
        ) : m.status === "failed" ? (
          <div
            key={m.id}
            className="chat__failure"
            data-testid="message-failed"
          >
            <Callout tone="critical" title={t("turn.error.title")} />
          </div>
        ) : m.id === rerolling && reroll ? (
          // F03-22: 기존 답변 자리에 새 답변(첫 글자 전엔 점 3개 + "고민하는 중")
          <CharacterTurn
            key={m.id}
            text={reroll.streamed}
            streaming
            speaker={speaker}
            caption={reroll.streamed ? undefined : t("reroll.thinking", { name: speakerName, josa: withJosa(speakerName, "이", "가") })}
          />
        ) : (
          <Fragment key={m.id}>
            <CharacterTurn
              text={m.content.text}
              streaming={m.status === "pending"}
              speaker={speaker}
              onLongPress={onLongPress}
              messageId={m.id}
              onRegenerate={m.id === rerollable ? () => onReroll?.(m.id) : undefined}
            />
            {reroll?.phase === "failed" && reroll.error && reroll.messageId === m.id && (
              <TurnFailure
                error={reroll.error}
                title={t("reroll.error.title")}
                note={t("reroll.error.kept")}
                onRetry={() => onRerollRetry?.()}
                onFocusInput={() => onFocusInput?.()}
              />
            )}
          </Fragment>
        ),
      )}
      {active(turn) && turn && (
        <>
          <UserTurn
            text={turn.text}
            pending={turn.phase === "sending"}
            onLongPress={onLongPress}
          />
          {(turn.phase === "accepted" ||
            turn.phase === "streaming" ||
            turn.phase === "recovering") && (
            <CharacterTurn
              text={
                turn.phase === "recovering" && !turn.streamed
                  ? t("turn.recovering")
                  : turn.streamed
              }
              streaming
              speaker={speaker}
              caption={turn.phase !== "recovering" && !turn.streamed ? t("turn.thinking", { name: speakerName, josa: withJosa(speakerName, "이", "가") }) : undefined}
            />
          )}
          {turn.phase === "failed" && turn.error && (
            <TurnFailure
              error={turn.error}
              onRetry={() => onRetry?.()}
              onFocusInput={() => onFocusInput?.()}
            />
          )}
        </>
      )}
      <div ref={endRef} />
    </section>
  );
}
